import { css } from '@emotion/react';
import { FC } from 'react';
import { Country, getCountries, getCountryCallingCode } from 'react-phone-number-input/input-core';

import { CountryLogo } from '~src/country/country-logo';
import { useCountryNamesTranslations } from '~src/country/country-names';
import { fontStyles } from '~src/theme/fontStyles';

import flags from '~metadata/country-flags.json';
import metadata from '~metadata/libphonenumber-metadata.custom.json';

export type CountrySelectElementProps = {
  country: Country;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const CountrySelectElement: FC<CountrySelectElementProps> = ({ country, onChange }) => {
  const { translate } = useCountryNamesTranslations();

  const sortedCountries = getCountries(metadata)
    .map(value => ({
      countryCode: value,
      country: translate(value) as string,
    }))
    .sort((a, b) => {
      return a.country < b.country ? -1 : 1;
    })
    .filter(value => value.country !== undefined);

  return (
    <div css={styles.selectWrapper}>
      <select css={styles.selectInput} value={country} onChange={onChange}>
        {sortedCountries.map(({ countryCode, country }) => (
          <option
            key={countryCode}
            value={countryCode}
            dangerouslySetInnerHTML={{ __html: `${flags[countryCode]} ${country}` }}
          />
        ))}
      </select>
      <div className="selectButton" css={styles.selectButton}>
        <CountryLogo countryCode={country} height={16} />
        <span css={styles.phonePrefix}>+{getCountryCallingCode(country, metadata)}</span>
        <div className="selectBorder" css={styles.selectBorder} />
      </div>
    </div>
  );
};

export const styles = {
  selectInput: css({
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    zIndex: 1,
    opacity: 0,
    cursor: 'pointer',
    '&.focus-visible + .selectButton > .selectBorder, &:focus + .selectButton > .selectBorder': {
      border: '2px dotted black',

      width: '100%',
      height: '100%',
    },
    WebkitAppearance: 'none', // safari fix
  }),
  selectBorder: css({
    position: 'absolute',
    top: '-3px',
    left: 0,
    width: '100%',
    transform: 'translateY(20%)',
  }),
  selectButton: css({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  }),
  selectWrapper: css({
    position: 'absolute',
    top: '28px',
    left: '16px',
    display: 'flex',
  }),
  phoneNumberField: css({
    position: 'relative',
    display: 'flex',
  }),
  phonePrefix: {
    marginLeft: '4px',
    ...fontStyles.px14_20,
  },
};
